/**
 * THE CATALOG — every node kind the world can contain, declared as data.
 *
 * See docs/node-graph-refactor-plan.md §2.1 and §2.5. Three properties make this worth having, and
 * all three are about not writing the same thing twice:
 *
 *  1. **The UI reads it.** `GenConfigEditor` is a recursive renderer over this array, so adding a
 *     knob is one line beside the generator that reads it and the world dialog grows for free. That
 *     dialog is 557 hand-authored lines today, reaching 74 of ~116 tunable fields.
 *  2. **The composition order is its flattening.** A child composes AFTER its parent and may read
 *     the parent's contribution; a parent knows nothing about its children. So the tree is the
 *     order, and `ORDER` below is derived rather than restated.
 *  3. **It is PURE DATA.** Nothing here imports TerrainGenerator, because the editor must be able to
 *     draw a form without constructing a world. The runtime kind specs are built next door with
 *     closures over a generator.
 *
 * THE TWO REGISTRIES ARE NOT HELD TO EACH OTHER YET, and this note used to claim they were. The
 * runtime resolver registers `scatter`, `stamp`, `city`, `block`, `building`, `floor`, `room`; this
 * declares `meadow` and seven named stamp leaves where the runtime has one `scatter` and one
 * generic `stamp`, and `stairs` where the runtime has none. Some of that gap is real and some of it
 * is deliberate — the runtime `scatter` node IS a chunk footprint (its exclusion rules are
 * chunk-local), so it serves every region type that has stamps and is NOT the meadow however much
 * the subtree under it looks like one. A cross-check is worth having once the field kinds are
 * registered too; asserting one that does not exist was worse than admitting it.
 *
 * WHAT IS DECLARED HERE IS AHEAD OF WHAT IS WIRED, by exactly the parameters listed in
 * `descriptors/city.ts`'s note (the BuildingPlan module constants). The guard names them, so the
 * list cannot grow quietly.
 */
import { TERRAIN_KINDS } from './descriptors/terrain.js';
import { REGION_KINDS } from './descriptors/regions.js';
import { MEADOW_KINDS } from './descriptors/meadow.js';
import { CITY_KINDS } from './descriptors/city.js';
export const CATALOG = [
    ...TERRAIN_KINDS,
    ...REGION_KINDS,
    ...MEADOW_KINDS,
    ...CITY_KINDS,
];
const BY_KIND = new Map(CATALOG.map((d) => [d.kind, d]));
export function descriptor(kind) {
    return BY_KIND.get(kind);
}
/** The root of the declared tree. Everything else is reachable from it through `children`. */
export const ROOT_KIND = 'world';
/**
 * The composition order: a depth-first walk of `children` from the root, parents before children.
 *
 * Computed once, and asserted against a written-down list in `columnPhases.test.ts` (C3) so that a
 * tree edit which reorders composition fails loudly instead of shifting output quietly.
 */
export const ORDER = (() => {
    const out = [];
    const seen = new Set();
    const walk = (kind) => {
        if (seen.has(kind))
            return;
        seen.add(kind);
        out.push(kind);
        for (const child of BY_KIND.get(kind)?.children ?? [])
            walk(child);
    };
    walk(ROOT_KIND);
    return out;
})();
/** The parent of a kind, or undefined for the root. Derived from `children`, never declared twice. */
export const PARENT = (() => {
    const m = new Map();
    for (const d of CATALOG)
        for (const c of d.children)
            m.set(c, d.kind);
    return m;
})();
//# sourceMappingURL=catalog.js.map